import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

/**
 * Semantic color → text token. Resting colors only (no hover) — the `Icon`-aligned
 * palette so text, icons, and clickable text all tone from the same vocabulary.
 */
const colorClasses = {
	brand: "text-brand-primary",
	accent: "text-brand-accent",
	link: "text-link",
	primary: "text-primary",
	secondary: "text-secondary",
	tertiary: "text-tertiary",
	subtle: "text-subtle",
	confirm: "text-confirm",
	warning: "text-intent-warning-text",
	danger: "text-danger",
};

export type TextColor = keyof typeof colorClasses;
export const TEXT_COLORS = Object.keys(colorClasses) as readonly TextColor[];

/**
 * Named type → font-size, line-height and font-weight. Each type pairs with an entry in
 * `Icon`'s `sizeMap` so text and icons tone from the same scale and sit together visually:
 * - `display` ↔ Icon `large`
 * - `heading` ↔ Icon `medium`
 * - `title` ↔ Icon `small`
 * - `body` / `body-strong` ↔ Icon `mini`
 * - `caption` / `eyebrow` ↔ Icon `micro`
 *
 * Omit to inherit the surrounding font size.
 */
const typeClasses = {
	display: "text-[24px] leading-[30px] font-bold",
	heading: "text-[20px] leading-[26px] font-semibold",
	title: "text-[16px] leading-[22px] font-semibold",
	body: "text-[14px] leading-[20px] font-normal",
	"body-strong": "text-[14px] leading-[20px] font-semibold",
	caption: "text-[12px] leading-[18px] font-medium",
	eyebrow: "text-[11px] leading-[16px] font-semibold uppercase",
};

export type TextType = keyof typeof typeClasses;
export const TEXT_TYPES = Object.keys(typeClasses) as readonly TextType[];

/**
 * Trim the font's half-leading so the box hugs cap-height → alphabetic baseline, and padding and
 * `gap` measure from the visible glyphs rather than the leading-padded line box. Descenders hang
 * below the box, so stacked `Text` needs a gap sized to the upper line (see the `Stacking` story).
 * `line-height` still drives wrapping. Takes effect on a flex/grid item or a `block`/`inline-block`
 * span; inline, the line box keeps its full height. Browsers without `text-box-trim` (Firefox
 * before 154) ignore it and keep the full line box.
 */
const trimToText = "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]";

const textVariants = cva(undefined, {
	variants: {
		color: colorClasses,
		type: typeClasses,
		// Truncating clips the box with `overflow: hidden`, which would cut the descenders off a
		// trimmed box, so one line of truncated text keeps the untrimmed line box instead.
		truncate: {
			true: "truncate",
			false: trimToText,
		},
	},
	defaultVariants: {
		truncate: false,
	},
});

type TextVariantProps = VariantProps<typeof textVariants>;

export type TextProps = HTMLAttributes<HTMLSpanElement> & {
	/**
	 * Semantic color of the text, from the `Icon`-aligned token map. Omit to inherit the
	 * surrounding text color.
	 */
	color?: TextVariantProps["color"];

	/**
	 * Typographic style of the text. Each type pairs with an entry in `Icon`'s `sizeMap`
	 * so text and icons tone from the same scale:
	 * - `display` ↔ Icon `large`
	 * - `heading` ↔ Icon `medium`
	 * - `title` ↔ Icon `small`
	 * - `body` / `body-strong` ↔ Icon `mini`
	 * - `caption` / `eyebrow` ↔ Icon `micro`
	 *
	 * Omit to inherit the surrounding font size.
	 */
	type?: TextVariantProps["type"];

	/**
	 * Clip overflow to a single line with a trailing ellipsis. Prefer this over a `truncate`
	 * class, which alone would let the clipped box cut the descenders off the visible glyphs.
	 * Takes effect once the span is blockified — a grid or flex item, `block`, `inline-block`.
	 * The truncated line keeps its untrimmed line box, about 5px taller per edge, so drop a
	 * stack's gap one step to match its trimmed neighbors.
	 */
	truncate?: boolean;

	/**
	 * Analytics identifier. Expands to `data-pendo-id` + `data-amp-track-id`.
	 */
	trackingId?: string;

	ref?: Ref<HTMLSpanElement>;
};

/**
 * Inline text primitive. A `<span>` that applies a semantic `color` and `type` from the
 * shared, `Icon`-aligned scales. Owns no interaction — compose it inside an interactive
 * primitive (e.g. `ClickableText`) when you need click/hover behavior.
 */
export const Text: FC<TextProps> = ({
	color,
	type,
	truncate,
	trackingId,
	className,
	ref,
	...rest
}) => (
	<span
		ref={ref}
		className={cn(textVariants({ color, type, truncate }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Text.displayName = "Text";
